<template>
    <div>
        <!-- Searchbar -->
        <div class="mb-4 mb-md-2" v-if="!disableTools">
            <div class="row">
                <div class="col-12 col-lg-6 order-2 order-lg-1">
                    <input
                        v-model="search"
                        :disabled="noDataAvailable"
                        type="text"
                        class="form-control"
                        placeholder="Search"
                    />
                </div>
                <div
                    class="col-12 col-lg-6 order-1 order-lg-2 mb-2 mb-lg-0 d-flex justify-content-lg-end align-items-center"
                >
                    <slot name="actions"></slot>
                </div>
            </div>

            <div class="row pt-1" v-if="filters.length > 0">
                <div class="col-12 d-flex align-items-center">
                    <div v-for="filter in filters" :key="filter.field" class="dropdown mr-2">
                        <a class="badge badge-gray" role="button" data-toggle="dropdown">
                            <span class="icon icon-glyphicons-basic-filter icon-mr"></span>
                            <span>{{ filter.label }}</span>
                            <span>:&nbsp;</span>
                            <span v-if="filtersApplied[filter.field]">{{ filtersApplied[filter.field].label }}</span>
                            <span v-else>All</span>
                        </a>
                        <div class="dropdown-menu shadow-sm filter-dropdown">
                            <a class="dropdown-item medium px-3" @click="setFilter(filter, null)">All</a>
                            <div v-show="filter.options.length > 0" class="dropdown-divider my-1"></div>
                            <a
                                v-for="option in filter.options"
                                :key="option[filter.valueField]"
                                class="dropdown-item medium px-3"
                                @click="setFilter(filter, option)"
                                >{{ option[filter.labelField] }}</a
                            >
                        </div>
                    </div>
                    <div v-show="filtersAreActive" class="ml-1">
                        <a class="small text-dark font-weight-500" @click="clearFilters()">
                            <span class="icon icon-glyphicons-basic-times icon-100 mr-1"></span>
                            <span>Clear Filters</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Large Screen Table -->
        <div class="row d-none d-md-block">
            <div class="col-12">
                <div class="table-responsive table-white border rounded">
                    <table class="table mb-0">
                        <!-- Table Header -->
                        <thead>
                            <tr>
                                <th v-if="config.multiSelect" class="align-middle check-column" scope="col">
                                    <div class="custom-control custom-checkbox">
                                        <input
                                            class="custom-control-input"
                                            @click="checkAll($event.target.checked)"
                                            type="checkbox"
                                            aria-label="Checkbox"
                                            id="checkall"
                                            v-model="checkAllCheckbox"
                                        />
                                        <label class="custom-control-label" for="checkall"></label>
                                    </div>
                                </th>
                                <th
                                    v-for="column in config.columns"
                                    :key="column.name"
                                    :class="{ hover: column.sort }"
                                    @click="setSortColumn(column)"
                                    scope="col"
                                >
                                    <div class="d-flex">
                                        <div class="flex-grow-1">
                                            <span :class="{ 'col-name': column.sort }">{{ column.name }}</span>
                                        </div>
                                        <div class="flex-shrink-0">
                                            <!-- if sort enabled - column active -->
                                            <div
                                                v-if="column.sort && sortColumn.name === column.name"
                                                class="d-flex align-items-center chevron-container"
                                            >
                                                <div class="d-flex flex-column">
                                                    <div class="chevron" :class="{ active: !sortDescend }">
                                                        <span class="icon-glyphicons-basic-chevron-up"></span>
                                                    </div>
                                                    <div class="chevron" :class="{ active: sortDescend }">
                                                        <span class="icon-glyphicons-basic-chevron-down"></span>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- if sort enabled - column inactive -->
                                            <div
                                                v-if="column.sort && sortColumn.name != column.name"
                                                class="d-flex align-items-center chevron-container"
                                            >
                                                <div class="d-flex flex-column">
                                                    <div class="chevron">
                                                        <span class="icon-glyphicons-basic-chevron-up"></span>
                                                    </div>
                                                    <div class="chevron">
                                                        <span class="icon-glyphicons-basic-chevron-down"></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </th>
                                <th v-if="config.actions" scope="col">Actions</th>
                            </tr>
                        </thead>

                        <!-- Table Body -->
                        <tbody>
                            <div v-show="loading" class="modal-loader-container center-over w-100 h-100 pt-3">
                                <div class="modal-loader center-over">
                                    <div class="loading">
                                        <div class="custom-spinner" role="status">
                                            <span class="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- No Data + Loading Spacer -->
                            <tr v-if="rows.length === 0 && loading">
                                <td :colspan="columnCount" class="text-center">
                                    <div style="min-height: 50px"></div>
                                </td>
                            </tr>

                            <!-- No Data Message -->
                            <tr v-if="noDataAvailable">
                                <td :colspan="columnCount" class="text-center">
                                    <div class="mb-2 mt-2 text-secondary">{{ emptyMessage }}</div>
                                </td>
                            </tr>

                            <!-- No Filtered Data Message -->
                            <tr v-if="noFilteredData && !noDataAvailable">
                                <td :colspan="columnCount" class="text-center">
                                    <div class="mb-2 mt-2 text-secondary">{{ emptyFilterMessage }}</div>
                                </td>
                            </tr>

                            <!-- Rows -->
                            <tr v-for="row in currentPageRows" :key="row.id">
                                <!-- Multi Select -->
                                <td v-if="config.multiSelect" class="align-middle">
                                    <div class="custom-control custom-checkbox">
                                        <input
                                            class="custom-control-input"
                                            v-model="row.checked"
                                            @click="checkRow(row, $event.target.checked)"
                                            type="checkbox"
                                            :id="'check' + row.id"
                                            aria-label="Checkbox"
                                        />
                                        <label class="custom-control-label" :for="'check' + row.id"></label>
                                    </div>
                                </td>

                                <!-- Data Columns -->
                                <td v-for="column in config.columns" :key="column.name" class="align-middle">
                                    <base-table-cell
                                        :row="row"
                                        :column="column"
                                        :max-items-per-column="maxItemsPerColumn"
                                        @addToSearch="addToSearch($event)"
                                    ></base-table-cell>
                                </td>

                                <!-- Actions -->
                                <td v-if="config.actions" class="align-middle">
                                    <base-table-actions
                                        :config="config"
                                        :row="row"
                                        @edit="$emit('edit', $event)"
                                        @delete="$emit('delete', $event)"
                                        @customEvent="$emit($event.name, $event.data)"
                                    ></base-table-actions>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Small Screen Table -->
        <div class="row d-block d-md-none">
            <div class="col-12">
                <ul class="list-group list-group-flush small-table">
                    <li v-show="loading" class="modal-loader-container center-over w-100 h-100">
                        <div class="modal-loader center-over">
                            <div class="loading">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="sr-only">Loading...</span>
                                </div>
                            </div>
                        </div>
                    </li>
                    <!-- No Data -->
                    <li
                        v-if="noDataAvailable"
                        class="list-group-item mb-2 mt-2 text-secondary text-center border-bottom-0"
                    >
                        {{ emptyMessage }}
                    </li>
                    <!-- Empty Filtered Data -->
                    <li
                        v-if="noFilteredData && !noDataAvailable"
                        class="list-group-item mb-2 mt-2 text-secondary text-center"
                    >
                        {{ emptyFilterMessage }}
                    </li>
                    <!-- Rows -->
                    <li
                        v-for="(row, index) in currentPageRows"
                        :key="row.id"
                        :class="{ 'top-item': index === 0 }"
                        class="list-group-item"
                    >
                        <!-- Row Data -->
                        <span v-for="column in config.columns" :key="column.name">
                            <base-table-cell
                                :row="row"
                                :column="column"
                                :max-items-per-column="maxItemsPerColumn"
                                @addToSearch="addToSearch($event)"
                            ></base-table-cell>
                        </span>
                        <!-- Actions -->
                        <div v-if="config.actions" class="mt-2">
                            <base-table-actions
                                :config="config"
                                :row="row"
                                @edit="$emit('edit', $event)"
                                @delete="$emit('delete', $event)"
                                @customEvent="$emit($event.name, $event.data)"
                            ></base-table-actions>
                        </div>
                    </li>
                </ul>
                <br />
            </div>
        </div>

        <!-- Rows displayed info text -->
        <div class="row align-items-center mt-3 mb-3" v-if="!disableTools">
            <div class="col-12 text-secondary d-flex flex-wrap align-items-center">
                <div class="flex-grow-1 medium">
                    <span v-show="rows.length && rowsDisplayed > 0">
                        Showing {{ currentPageStartIndex + 1 }} to {{ rowIndexEnd }} of {{ totalResultsFilteredCount }}
                        <span v-show="searchIsActive">matching</span> entries.
                    </span>
                    <span v-show="rows.length && rowsDisplayed === 0"> Showing 0 of {{ rows.length }} entries. </span>
                    <span v-show="rowsCheckedCount > 0"> {{ rowsCheckedCount }} rows selected. </span>
                </div>
                <div class="flex-grow-0">
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div class="row">
            <div v-show="showPagination" class="col-12">
                <div class="d-flex flex-column flex-md-row align-items-md-center">
                    <div class="flex-grow-0 mr-3">
                        <nav aria-label="Table pagination" class="">
                            <ul class="pagination pagination-sm flex-wrap mb-0">
                                <li :class="{ disabled: currentPage === 1 }" class="page-item pagination-first mb-1">
                                    <a @click="moveToLastPage()" class="page-link" tabindex="-1">
                                        <span class="icon icon-glyphicons-basic-chevron-left"></span>
                                    </a>
                                </li>
                                <li
                                    v-if="showExpandPagesBefore"
                                    class="page-item mb-1 float-left"
                                    @click="expandPaginationLeft()"
                                >
                                    <a class="page-link" tabindex="-1">...</a>
                                </li>
                                <li
                                    v-for="page in pagesInViewArray"
                                    :key="page"
                                    :class="{ active: currentPage === page }"
                                    class="page-item mb-1 float-left"
                                >
                                    <a @click="currentPage = page" class="page-link" tabindex="-1">{{ page }}</a>
                                </li>
                                <li
                                    v-if="showExpandPagesAfter"
                                    class="page-item mb-1 float-left"
                                    @click="expandPaginationRight()"
                                >
                                    <a class="page-link" tabindex="-1">...</a>
                                </li>
                                <li :class="{ disabled: currentPage === pageCount }" class="page-item mb-1">
                                    <a @click="moveToNextPage()" class="page-link" tabindex="-1">
                                        <span class="icon icon-glyphicons-basic-chevron-right"></span>
                                    </a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div v-if="rowsPerPageOptions.length > 0" class="flex-grow-0 flex-shrink-0 mb-1">
                        <div class="d-flex align-items-center">
                            <select
                                class="form-control form-control-sm"
                                v-model="rowsPerPageSelected"
                                @change="resetCurrentPage()"
                            >
                                <option v-for="option in rowsPerPageOptions" :key="option" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                            <div class="ml-2 text-secondary nowrap">
                                <span>rows per page</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Utilities from "@/assets/js/utilities.js"
import BaseTableCell from "@/components/BaseTableCell"
import BaseTableActions from "@/components/BaseTableActions"

export default {
    name: "BaseTable",
    components: {
        BaseTableCell,
        BaseTableActions,
    },
    props: {
        // An array of checked items.
        value: {
            type: Array,
            required: false,
        },
        // An array containing the data for the table.
        tableData: {
            type: Array,
            required: true,
        },
        // The maximum number of rows to display on a single page of the table, after which
        // point pagination will be used.
        rowsPerPage: {
            type: Number,
            default: 15,
        },
        rowsPerPageOptions: {
            type: Array,
            required: false,
            default() {
                return []
            },
        },
        // The maximum number of pages to viewable as pagination buttons at one time.
        maxPages: {
            type: Number,
            default: 20,
        },
        // When true, no client-side filtering, sorting, or paginating will be applied to the
        // provided table data.
        serverSideSearchMode: {
            type: Boolean,
            default: false,
        },
        // Only used with server side search mode. Used to display the total number of results
        // for a given query, even though only a subset have been loaded into the front end.
        serverSideTotalResults: {
            type: Number,
            default: null,
        },
        // When true, a loading spinner is displayed over the table (used with server side
        // search mode).
        loading: {
            type: Boolean,
            default: false,
        },
        // The text to display if the table is empty.
        emptyMessage: {
            type: String,
            default: "No items to display.",
        },
        // If true, the searchbar, top buttons, and pagination text will be hidden.
        disableTools: {
            type: Boolean,
            default: false,
        },
        // For list-type columns, this number represents the maximum number of items that
        // are displayed before an expand icon is shown
        maxItemsPerColumn: {
            type: Number,
            default: 5,
        },
        // An object for configuring columns, actions, and other behaviour.
        // Supported column types are List, Date, or unspecified.
        config: {
            type: Object,
            required: true,
            validator: (value) => {
                // initialSort - which property to sort by initially
                if (!value.initialSort) {
                    return false
                }
                // initialSortDescend - if true, initially sorts in descending order
                if (value.initialSortDescend === undefined) {
                    value.initialSortDescend = true
                }
                // multiSelect - if true, selection of rows
                if (!value.multiSelect) {
                    value.multiSelect = false
                }
                // validCondition - defines a property to test whether the row is valid, displays info icon beside invalid rows
                if (!value.validCondition) {
                    value.validCondition = null
                }
                // actions - an object controlling the display of edit and delete buttons on each row
                if (!value.actions) {
                    value.actions = null
                }
                // customActions - an array of objects defining custom action buttons on each row, each containing a name and event to emit
                if (!value.customActions) {
                    value.customActions = []
                }
                // columns - array of objects defining the configuration for each column, each containing:
                // name: required, string to display as title of column
                // sort: required, whether or not to enable sorting on the column
                // sortKey: optional, used to specify a key to sort the column by - required with type: 'date'
                // key: required without providing display, property used to display entries in the column
                // display: required without providing key, function returning a formatted string to display entries in the column
                // link: optional, function returning a path used to format the column as a link
                // emptyDisplay: optional, a string to display if no data
                // type: optional, can be 'html' to format display output as html, or 'date' to treat as date
                // classes: optional, a string of classes to be applied to the column
                // elements: a function for displaying a list of items - required with type: 'list'
                // listClass: a function that accepts a list element, and returns a class to add to the element - required with type: 'list
                if (!value.columns) {
                    return false
                }
                return true
            },
        },
    },
    data() {
        return {
            sortColumn: {
                name: null,
                key: null,
                sortKey: null,
            },
            sortDescend: false,
            search: "",
            currentPage: 1,
            paginationPageStart: 1,
            checkedRows: {},
            emptyFilterMessage: "No items matching your search",
            rows: [],
            serverSearchLoading: false,
            filtersApplied: {},
            rowsPerPageSelected: this.rowsPerPage,
            checkAllCheckbox: false,
        }
    },
    created() {
        let vm = this
        // set sorting direction
        this.sortDescend = this.config.initialSortDescend ? true : false
        // find sort column based on name provided in config
        this.sortColumn = this.config.columns.find((col) => {
            return col.name === vm.config.initialSort
        })
        // reject if named sort column not found in config
        if (!this.sortColumn) {
            throw "Provided initial sort column not found in column config."
        }
    },
    mounted() {
        this.updateRows()
    },
    updated() {
        // trigger enabling of tooltips
        $('[data-toggle="tooltip"]').tooltip()
    },
    watch: {
        // watch for changes in table data prop, regenerate rows on change
        tableData: {
            handler() {
                this.updateRows()
                if (this.serverSideSearchMode) {
                    this.resetCheckAll()
                }
            },
            deep: true,
        },
        search: function (newValue) {
            this.currentPage = 1
            this.$emit("search", newValue)
            if (this.serverSideSearchMode) {
                this.resetCheckAll()
            }
        },
        currentPageStartIndex: function (newValue) {
            this.$emit("update:pageStart", newValue)
            if (this.serverSideSearchMode) {
                this.resetCheckAll()
            }
        },
        currentPageMaxIndex: function (newValue) {
            this.$emit("update:pageEnd", newValue)
            if (this.serverSideSearchMode) {
                this.resetCheckAll()
            }
        },
        sortColumnKey: function (newValue) {
            this.$emit("update:sortColumn", newValue)
            if (this.serverSideSearchMode) {
                this.resetCheckAll()
            }
        },
        sortDescend: function (newValue) {
            this.$emit("update:sortDescend", newValue)
            if (this.serverSideSearchMode) {
                this.resetCheckAll()
            }
        },
        filters: function () {
            this.resetCurrentPage()
        },
    },
    computed: {
        // Table rows, filtered by client-side search input.
        rowsFilteredClientSide() {
            // transform the full search string to lower case
            let searchString = this.search.toLowerCase()
            // split at spaces, ignoring spaces within quotes
            let searchComponents = searchString.match(/(?:[^\s"]+|"[^"]*")+/g)

            if (!searchComponents) {
                return this.rows
            }

            let rows = this.rows
            searchComponents.forEach(function (component) {
                if (component.includes(":")) {
                    // search component contains a colon, so do a field search
                    let searchPair = component.split(":")
                    let searchCol = searchPair[0]
                    let searchValue = searchPair[1].replace(/"/g, "")
                    // filter on the specific column, look for an exact match
                    rows = rows.filter((row) => {
                        if (!row.searchFields[searchCol]) {
                            return false
                        }
                        return row.searchFields[searchCol].indexOf(searchValue) != -1
                    })
                } else {
                    // search against the search text for matches
                    rows = rows.filter((row) => row.searchString.includes(component))
                }
            })
            return rows
        },
        // The rows of the current page being viewed.
        currentPageRows() {
            if (this.serverSideSearchMode) {
                return this.rows
            }
            return this.rowsFilteredClientSide.slice(this.currentPageStartIndex, this.currentPageMaxIndex)
        },
        // The total number of pages of rows filtered by the search query.
        pageCount() {
            if (this.serverSideSearchMode) {
                return Math.floor((this.totalResultsCount - 1) / this.rowsPerPageSelected) + 1
            }
            return Math.floor((this.rowsFilteredClientSide.length - 1) / this.rowsPerPageSelected) + 1
        },
        // Whether or not to show pagination tools. Only show if the number of pages is
        // greater than 1.
        showPagination() {
            return this.pageCount > 1
        },
        // Current viewable pages, as an array of numbers, to be the pagination toolbar as tabs.
        pagesInViewArray() {
            let start = this.paginationPageStart
            let end = Math.min(this.pageCount, this.paginationPageStart + this.maxPages - 1)
            let pagesInViewArray = []
            for (let i = start; i <= end; i++) {
                pagesInViewArray.push(i)
            }
            return pagesInViewArray
        },
        // Determines whether to show the three dots option at the end of pagination, to view the
        // next page in sequence.
        showExpandPagesAfter() {
            let lastPageNumberInView = this.pagesInViewArray[this.pagesInViewArray.length - 1]
            return lastPageNumberInView < this.pageCount
        },
        // Determines whether to show the three dots option at the beginning of pagination.
        // Only show if the first page number shown in the
        showExpandPagesBefore() {
            return this.paginationPageStart > 1
        },
        // The index of the first row in the current page being viewed.
        currentPageStartIndex() {
            return (this.currentPage - 1) * this.rowsPerPageSelected
        },
        // The index of the highest row in the current page being viewed.
        currentPageMaxIndex() {
            return this.currentPage * this.rowsPerPageSelected
        },
        // The actual index of the last row in the current page being viewed.
        rowIndexEnd() {
            return Math.min(this.currentPageMaxIndex, this.totalResultsFilteredCount)
        },
        // The number of rows currently being displayed.
        rowsDisplayed() {
            return this.currentPageRows.length
        },
        // The total available rows of data, before any filtering.
        totalResultsCount() {
            if (this.serverSideSearchMode) {
                return this.serverSideTotalResults
            }
            return this.rows.length
        },
        // The total number of results filtered by the search bar.
        totalResultsFilteredCount() {
            if (this.serverSideSearchMode) {
                return this.serverSideTotalResults
            }
            return this.rowsFilteredClientSide.length
        },
        // True if there's no data to display in the table.
        noDataAvailable() {
            // Never set this to true if using server side mode.
            if (this.serverSideSearchMode) {
                return false
            }
            return this.totalResultsCount === 0 && this.search === ""
        },
        // True if there's no data to display after filtering.
        noFilteredData() {
            if (this.serverSideSearchMode) {
                return this.serverSideTotalResults === 0
            }
            return this.rowsFilteredClientSide.length === 0 && !this.noDataAvailable
        },
        // The number of columns in the table.
        columnCount() {
            let extraCols = 0
            if (this.config.actions) {
                extraCols++
            }
            if (this.config.multiSelect) {
                extraCols++
            }
            return this.config.columns.length + extraCols
        },
        // The key of the currently selected sort column.
        sortColumnKey() {
            return this.sortColumn.key
        },
        // The list of filters for the table, extracted from the table config.
        filters() {
            return this.config.filters || []
        },
        // Returns true if at least one filter is applied/active.
        filtersAreActive() {
            let active = false
            let vm = this
            for (const key in this.filtersApplied) {
                if (vm.filtersApplied[key]) {
                    active = true
                    break
                }
            }
            return active
        },
        // Returns the number of currently checked/selected rows.
        rowsCheckedCount() {
            if (!this.value) {
                return 0
            }
            return this.value.length
        },
        searchIsActive() {
            return Boolean(this.search)
        },
    },
    methods: {
        // Set a column for sorting.
        setSortColumn(col) {
            if (!col.sort) {
                return
            }
            this.sortColumn = col
            // toggle sort order if same column clicked
            if (this.sortColumn.name === col.name) {
                this.sortDescend = !this.sortDescend
            }
            if (!this.serverSideSearchMode) {
                sortRows(this.rows, this.sortColumn, this.sortDescend)
            }
        },
        // Set a row to checked.
        checkRow(row, value) {
            row.checked = value
            this.updateCheckedArray()
        },
        // Set all rows to checked.
        // TODO: this should only check the rows in view
        checkAll(checked) {
            this.rows.forEach((row) => {
                row.checked = checked
            })
            this.updateCheckedArray()
        },
        resetCheckAll() {
            this.checkAllCheckbox = false
            this.checkAll(false)
        },
        // Updates the array of checked rows in the v-model of the parent.
        updateCheckedArray() {
            let checkedArray = []
            this.rows.forEach((row) => {
                if (row.checked) {
                    checkedArray.push(row)
                }
            })
            this.$emit("input", checkedArray)
        },
        // Turn the provided table data into formatted rows.
        updateRows() {
            if (!this.tableData || this.tableData.length === 0) {
                this.rows = []
            }

            // Create a clone of the table data for us to manipulate.
            let rows = JSON.parse(JSON.stringify(this.tableData))
            let vm = this

            // Add search fields.
            rows.forEach((row) => {
                row.searchString = ""
                row.searchFields = {}
                vm.config.columns.forEach((col) => {
                    let display = ""
                    let valuesList = null
                    // Add searchable string property using provided display function.
                    if (col.type === "list") {
                        valuesList = col.elements(row).map((elem) => col.display(elem).toLowerCase())
                        display = col
                            .elements(row)
                            .map((elem) => col.display(elem))
                            .join()
                    } else if (col.display) {
                        display = col.display(row).toLowerCase()
                        valuesList = [display]
                    } else if (Utilities.getNestedProperty(col.key, row)) {
                        display = String(Utilities.getNestedProperty(col.key, row)).toLowerCase()
                        valuesList = [display]
                    }
                    // Transform display text to lower case.
                    display = display.toLowerCase()
                    // Add to general search string.
                    row.searchString = row.searchString + display
                    // Add display to search fields for specific column searching.
                    row.searchFields[col.name.toLowerCase()] = valuesList
                })
            })
            if (!this.serverSideSearchMode) {
                sortRows(rows, this.sortColumn, this.sortDescend)
            }
            vm.rows = rows
        },
        moveToNextPage() {
            this.currentPage++
            // If the current page being viewed falls outside of the pages in the pagination toolbar,
            // expand the pagination items to the right.
            if (this.pagesInViewArray.indexOf(this.currentPage) == -1) {
                this.expandPaginationRight()
            }
        },
        moveToLastPage() {
            this.currentPage--
            // If the current page being viewed falls outside of the pages in the pagination toolbar,
            // expand the pagination items to the left.
            if (this.pagesInViewArray.indexOf(this.currentPage) == -1) {
                this.expandPaginationLeft()
            }
        },
        // Switch to the first page.
        resetCurrentPage() {
            this.currentPage = 1
        },
        // Increase the current list of pages being viewed in the pagination toolbar.
        expandPaginationRight() {
            this.paginationPageStart += 1
        },
        // Decrease the current list of pages being viewed in the pagination toolbar.
        expandPaginationLeft() {
            if (this.paginationPageStart <= 1) {
                return
            }
            this.paginationPageStart -= 1
        },
        // Set a filter to the provided option.
        setFilter(filter, option) {
            this.currentPage = 1
            let filterValue = null
            if (option) {
                filterValue = {
                    value: option[filter.valueField],
                    label: option[filter.labelField],
                }
            }
            this.$set(this.filtersApplied, filter.field, filterValue)
            this.$emit("update:filtersApplied", this.filtersApplied)
        },
        // Clear all currently applied filters.
        clearFilters() {
            this.currentPage = 1
            this.filtersApplied = {}
            this.$emit("update:filtersApplied", this.filtersApplied)
        },
        addToSearch(text) {
            let searchString = text.toLowerCase()
            // Don't add the text if it already matches what's in the searchbar.
            if (this.search == searchString) {
                return
            }
            if (this.search) {
                // Add the text with the plus syntax.
                this.search = this.search + " " + searchString
            } else {
                // No search text exists, so replace the search text.
                this.search = searchString
            }
        },
    },
}

let sortRows = (rows, sortColumn, descending) => {
    if (sortColumn.type === "date") {
        // Sort by date.
        rows.sort(compareDates(sortColumn.sortKey, descending))
    } else {
        if (sortColumn.sortKey) {
            // Sort by display.
            rows.sort(compareCustom(sortColumn.sortKey, descending))
        } else if (sortColumn.display) {
            // Sort by display.
            rows.sort(compareCustom(sortColumn.display, descending))
        } else {
            // Sort by key.
            rows.sort(compareBasic(sortColumn.key, descending))
        }
    }
}

// Returns a basic compare function to be used with array sorting.
let compareBasic = (key, descend) => (a, b) => {
    if (Utilities.getNestedProperty(key, a) < Utilities.getNestedProperty(key, b)) {
        return descend === true ? 1 : -1
    }
    if (Utilities.getNestedProperty(key, a) > Utilities.getNestedProperty(key, b)) {
        return descend === true ? -1 : 1
    }
    return 0
}

// Returns a custom compare function to be used with array sorting.
let compareCustom = (custom, descend) => (a, b) => {
    if (custom(a) < custom(b)) {
        return descend === true ? 1 : -1
    }
    if (custom(a) > custom(b)) {
        return descend === true ? -1 : 1
    }
    return 0
}

// Returns a date compare function to be used with array sorting.
let compareDates = (key, desc) => (a, b) => {
    var dateA = new Date(a[key])
    var dateB = new Date(b[key])
    if (dateA < dateB) {
        return desc === true ? 1 : -1
    }
    if (dateA > dateB) {
        return desc === true ? -1 : 1
    }
    return 0
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

$alt-row-background: #edeff3;
$alt-row-background-hover: #e6e9ef;
$alt-row-background-sm: #f9f9fb;

table {
    font-size: 14px;
    thead th {
        border-bottom: 0px;
    }
}

tbody tr:nth-of-type(odd) {
    background-color: $alt-row-background;
}

tbody {
    tr {
        &:hover {
            background-color: $alt-row-background-hover !important;
        }
    }
}

td {
    padding: 7px;
}

th {
    padding: 8px;
    border: none;
    white-space: nowrap;

    .chevrons {
        display: inline-block;
        margin-left: -25px;
    }

    .col-name {
        margin-right: 25px;
    }
}

.small-table {
    li {
        padding-left: 8px;
        padding-right: 8px;
        padding-top: 8px;
        padding-bottom: 8px;
    }

    li:nth-child(even) {
        background: $alt-row-background-sm;
    }
}

.check-column {
    width: 28px;
}

.chevron-icon {
    height: 16px;
    color: inherit;
    font-size: 1rem;
}

.hover {
    cursor: pointer;
}

.chevron-container {
    height: 6px;
}

.chevron {
    height: 8px;
    color: $gray-500;
}

.active {
    color: black;
}

.btn-toolbar {
    flex-wrap: nowrap;
    display: flex;
}

.pagination-first {
    margin-left: -1px;
}

.page-item {
    cursor: pointer;
}

.table-white {
    background-color: #ffffff;
}

.modal-loader-container {
    text-align: center;
    background-color: #ffffff7d;
    z-index: 1080;
}

.modal-loader {
    display: inline-block;
}

.center-over {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.filter-dropdown {
    max-height: 500px;
    overflow-y: scroll;
}

.nowrap {
    white-space: nowrap;
}
</style>